import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { cache } from '@common/util/cache.operator';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { PubSubService } from '@portal-core/general/services/pub-sub.service';
import { LicensesService } from '@portal-core/licenses/services/licenses.service';
import { SiteProfile } from '@portal-core/profiles/models/site-profile.model';
import { ProfilesService } from '@portal-core/profiles/services/profiles.service';
import { SiteSettingsFormComponent } from '@portal-core/sites/components/site-settings-form/site-settings-form.component';
import { SitesService } from '@portal-core/sites/services/sites.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { LoadingState } from '@portal-core/util/loading-state';
import { BehaviorSubject, combineLatest, first, Observable, switchMap } from 'rxjs';

export interface SiteProfileDialogData {
  siteId: number;
  siteProfileTab?: SiteProfileTab;
}

export enum SiteProfileTab {
  Delete,
  Access,
  Settings
}

@Component({
  selector: 'mc-site-profile-dialog',
  templateUrl: './site-profile-dialog.component.html',
  styleUrls: ['./site-profile-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteProfileDialogComponent extends DialogBase implements OnInit {
  static DialogConfig: MatDialogConfig = {
    width: '90rem',
    height: '71rem',
    autoFocus: false
  };

  @ViewChild('settingsForm', { static: false }) settingsFormComponent: SiteSettingsFormComponent;

  SiteProfileTab: typeof SiteProfileTab = SiteProfileTab;
  loadingState: LoadingState<string> = new LoadingState<string>();
  siteProfileTab: SiteProfileTab;
  site$: Observable<SiteProfile>;
  liveSiteUrl$: Observable<string>;
  private reload$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  needsDelayedReload: boolean = false;

  constructor(
    protected dialog: MatDialog,
    protected dialogRef: MatDialogRef<SiteProfileDialogComponent>,
    private profilesService: ProfilesService,
    private errorService: ErrorService,
    private licensesService: LicensesService,
    private sitesService: SitesService,
    private pubSubService: PubSubService,
    @Inject(MAT_DIALOG_DATA) public data: SiteProfileDialogData
  ) {
    super(dialog, dialogRef);
  }

  get requirePromptOnClose(): boolean {
    return this.settingsFormComponent?.siteSettingsForm?.dirty;
  }

  ngOnInit() {
    super.ngOnInit();

    this.loadingState.update(true);
    this.site$ = this.reload$.pipe(
      switchMap(() => this.profilesService.getSiteProfile$(this.data.siteId)),
      cache()
    );

    this.liveSiteUrl$ = this.site$.pipe(
      switchMap(site => this.licensesService.getItemById$(site.LicenseId)),
      switchMap(license => this.sitesService.getLiveSiteUrl$(license.VanityBaseUrl))
    );

    combineLatest([
      this.site$,
      this.liveSiteUrl$
    ]).pipe(
      first()
    ).subscribe(() => {
      this.siteProfileTab = this.data.siteProfileTab != null ? this.data.siteProfileTab : SiteProfileTab.Settings;
      this.loadingState.update(false);
    }, error => {
      this.loadingState.update(false, 'Failed to load the site.', this.errorService.getErrorMessages(error));
    });

    this.dialogRef.afterClosed().pipe(first()).subscribe(dialog => {
      if (this.needsDelayedReload) {
        this.pubSubService.publish('site-updated', { siteId: this.data.siteId });
      }
    });

  }

  onSaved(siteProfileForm: SiteProfileTab) {
    switch (siteProfileForm) {
      case SiteProfileTab.Access:
        this.needsDelayedReload = true;
        break;
      default:
        this.reload$.next();
        break;
    }
  }
}
